'use client';

import type { MutableRefObject } from 'react';
import React from 'react';
import { useId } from 'react-aria';
import styled, { keyframes } from 'styled-components';
import { theme } from '@frameio/vapor-theme';
import { Text } from 'components/Text';
import { Box } from 'components/Box';

type SpinnerSizes = 'xx-small' | 'x-small' | 'small' | 'medium' | 'large';

type SpinnerVariants = 'default' | 'brand' | 'brandMulti' | 'brandOverlay';

type AccessibleLabelProps =
  | { label: string; 'aria-label'?: never; 'aria-labelledby'?: never }
  | { label?: never; 'aria-label': string; 'aria-labelledby'?: never }
  | { label?: never; 'aria-label'?: never; 'aria-labelledby': string };

export type SpinnerProps = {
  /** Whether or not to display the spinner. */
  isLoading: boolean;
  /** Delay (in ms) from when isLoading is true, and the spinner fades in. */
  delay?: number;
  /** Duration for the enter animation in milliseconds */
  enterDuration?: number;
  /** @deprecated exit animations are now disabled. This props will have no
   * effect. */
  exitDuration?: number;
  /** Size of the spinner. */
  size?: SpinnerSizes;
  /**
   * Centers itself within a relative container using absolute positioning. If a number is
   * provided, it centers and uses that number as a y offset.
   */
  centered?: boolean | number;
  /** All spinners using this prop will be synchronized, regardless of when they were first mounted. */
  syncIndeterminant?: boolean;
  /** The color variant of the spinner. */
  variant?: SpinnerVariants;
} & React.HTMLAttributes<HTMLDivElement> &
  AccessibleLabelProps;

/**
 * A spinner component that can be used to indicate loading or processing.
 *
 * @example
 * ```jsx
 * <Spinner
 *  isLoading={isLoading}
 *  aria-label="Loading"
 *  size="medium"
 *  delay={0}
 *  centered
 * />
 * ```
 */

export const Spinner = React.forwardRef(
  (
    { isLoading, ...props }: SpinnerProps,
    ref: MutableRefObject<HTMLDivElement>
  ) => {
    if (!isLoading) return null;
    return <SpinnerSvg {...props} ref={ref} isLoading={true} />;
  }
);

const SpinnerSvg = React.forwardRef(
  (
    {
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledBy,
      label,
      centered,
      delay = 1000,
      enterDuration = 720,
      isLoading,
      size = 'medium',
      variant = 'default',
      style,
      ...other
    }: SpinnerProps,
    forwardedRef: MutableRefObject<HTMLDivElement>
  ) => {
    const [circleSize, strokeWidth] = getSizeAndStrokeWidth(size);

    const gradientId = useId();
    const maskId = useId();

    const circleDimensions: React.SVGProps<SVGCircleElement> = {
      cx: '50%',
      cy: '50%',
      r: `calc(50% - ${strokeWidth / 2}px)`,
      strokeWidth,
    };

    const ringAttributes: React.SVGProps<SVGCircleElement> = {
      ...circleDimensions,
      pathLength: 100,
      strokeLinecap: 'round',
      fill: 'none',
      strokeDasharray: '100 200',
      transform: 'rotate(-45)',
    };

    return (
      <StyledFadeContainer
        ref={forwardedRef}
        aria-busy={isLoading}
        aria-label={ariaLabel || label}
        aria-labelledby={ariaLabelledBy}
        aria-live="polite"
        data-centered={centered || undefined}
        data-chromatic="ignore"
        role="progressbar"
        style={{
          ...style,
          animationDelay: `${delay}ms`,
          animationDuration: `${enterDuration}ms`,
          width: circleSize,
          height: circleSize,
        }}
        {...other}
      >
        <Rings fill="none" height={circleSize} width={circleSize}>
          <defs>
            <linearGradient id={gradientId} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor="#000" />
              <stop offset="100%" stopColor="#fff" />
            </linearGradient>
            <mask id={maskId}>
              <rect fill={`url(#${gradientId})`} height="100%" width="100%" />
            </mask>
          </defs>
          <circle
            {...circleDimensions}
            stroke={theme.colors.components.spinner[variant].trackColor}
            strokeWidth={strokeWidth}
          />
          <g>
            <RingStroke
              stroke={theme.colors.components.spinner[variant].stopColorEnd}
              {...ringAttributes}
            />
          </g>
          <g mask={`url(#${maskId})`}>
            <RingStroke
              opacity="0.9"
              stroke={theme.colors.components.spinner[variant].stopColorBegin}
              {...ringAttributes}
            />
          </g>
        </Rings>
        {label && (
          <Box
            position="absolute"
            left="50%"
            top="calc(100% + 8px)"
            transform="translateX(-50%)"
          >
            <Text textAlign="center" color="tertiary" variant="body">
              {label}
            </Text>
          </Box>
        )}
      </StyledFadeContainer>
    );
  }
);

function getSizeAndStrokeWidth(size: SpinnerSizes) {
  switch (size) {
    case 'large':
      return [64, 6];
    case 'medium':
      return [32, 3];
    case 'small':
      return [24, 2];
    case 'x-small':
      return [14, 2];
    case 'xx-small':
      return [12, 2];
    default:
      return [24, 4];
  }
}

const fadeInKeyframes = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const StyledFadeContainer = styled.div`
  animation-fill-mode: forwards;
  animation-name: ${fadeInKeyframes};
  animation-timing-function: ${theme.transitions.eases.easeInOut};
  opacity: 0;
  pointer-events: none;
  position: relative;

  &[data-centered] {
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
  }
`;

const rotate = keyframes`
  0% {
    transform: rotate(0deg);
  }
  50% {
    transform: rotate(320deg);
  }
  100% {
    transform: rotate(720deg);
  }
`;

const Rings = styled.svg`
  animation: ${rotate} 1.4s linear infinite;
`;

const dashoffset = keyframes`
  0%, 100% {
    stroke-dashoffset: 96;
    transform: rotate(0deg);
  }

  30% {
    stroke-dashoffset: 40;
    transform: rotate(-106deg);
  }
`;

const RingStroke = styled.circle`
  animation: ${dashoffset} 1.4s linear infinite;
  transform-origin: center;
`;
